import React from 'react';

import { buildLogoUrl } from '../helpers/branding/common';
import type { NodeLogoKey } from '../helpers/branding/types';

export interface IBrandingContext {
  footerCopyright?: string;
  orderExecutionPolicyUrl?: string;
  windowTitle?: string;
  logoBaseUrl?: string;
  /**
   * Builds a cache-busted logo URL, defaulting to the firm's own node. Cache-busting is only
   * applied for the firm node, whose upload timestamps this provider tracks.
   */
  getLogoUrl: (key: NodeLogoKey, nodeId?: string) => string | undefined;
  /** Records a fresh upload timestamp for a firm-node logo so its URL cache-busts. */
  setLogoLastModified: (key: NodeLogoKey, value: string | number | Date) => void;
}

export const BrandingContext = React.createContext<IBrandingContext>({
  getLogoUrl: () => undefined,
  setLogoLastModified: () => undefined,
});

export interface IBrandingProviderProps {
  /** The firm's own node, used to resolve its default logo URLs. */
  nodeId?: string;
  footerCopyright?: string;
  orderExecutionPolicyUrl?: string;
  windowTitle?: string;
  logoBaseUrl?: string;
}

export const BrandingProvider: React.FC<React.PropsWithChildren<IBrandingProviderProps>> = ({
  nodeId,
  footerCopyright,
  orderExecutionPolicyUrl,
  windowTitle,
  logoBaseUrl,
  children,
}) => {
  const [logoTimestamps, setLogoTimestamps] = React.useState<Partial<Record<NodeLogoKey, string>>>({});

  const setLogoLastModified = React.useCallback((key: NodeLogoKey, value: string | number | Date) => {
    setLogoTimestamps(prev => ({ ...prev, [key]: new Date(value).getTime().toString() }));
  }, []);

  const getLogoUrl = React.useCallback(
    (key: NodeLogoKey, targetNodeId = nodeId): string | undefined => {
      if (!logoBaseUrl || !targetNodeId) return undefined;
      const lastModified = targetNodeId === nodeId ? logoTimestamps[key] : undefined;
      return buildLogoUrl(logoBaseUrl, targetNodeId, key, lastModified);
    },
    [logoBaseUrl, nodeId, logoTimestamps]
  );

  const value = React.useMemo<IBrandingContext>(
    () => ({
      footerCopyright,
      orderExecutionPolicyUrl,
      windowTitle,
      logoBaseUrl,
      getLogoUrl,
      setLogoLastModified,
    }),
    [footerCopyright, orderExecutionPolicyUrl, windowTitle, logoBaseUrl, getLogoUrl, setLogoLastModified]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBranding = (): IBrandingContext => React.useContext(BrandingContext);
